import { Badge, Box, Flex, Tooltip } from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  formatDate,
  Link,
  TruncatedTextWithTooltip,
} from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  useDeleteRequestsForInvoicingBulkMutation,
  useSubmitRequestsForInvoicingBulkMutation,
} from "@src/__generated__/graphql";
import {
  RequestForInvoicingOrderByColumnEnum,
  RequestForInvoicingStatusEnum,
  type RequestForInvoicingTypeEnum,
} from "@src/__generated__/urql-graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import type { BulkAction } from "@src/components/widgets/bulk-actions/types";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import { last, map, startCase } from "lodash";
import { observer } from "mobx-react-lite";
import React, { useContext, useMemo, useState } from "react";
import type { RequestForInvoicingModel } from "../models";
import { PageRequestsForInvoicingCtx } from "../PageRequestsForInvoicing";
import { PopoverRowActions } from "./PopoverRowActions";

export type TData = {
  id: RequestForInvoicingModel["id"];
  ownerId: RequestForInvoicingModel["ownerId"];
  brands: RequestForInvoicingModel["brands"];
  projects: RequestForInvoicingModel["projects"];
  totalAmount: RequestForInvoicingModel["stats"]["total_amount"];
  totalAmountWithVAT: RequestForInvoicingModel["stats"]["total_amount_with_vat"];
  created: RequestForInvoicingModel["created_at"];
  status: RequestForInvoicingStatusEnum;
  type: RequestForInvoicingTypeEnum;
  dateOfSupply: RequestForInvoicingModel["date_of_supply"];
  submitDate: RequestForInvoicingModel["submitDate"];
  note: RequestForInvoicingModel["note"];
  internalNote: RequestForInvoicingModel["internal_note"];
  paymentMethod: RequestForInvoicingModel["invoicePaymentMethod"];
  actions: string;
};

const getColumns = (): BodyColumnDef<
  TData,
  RequestForInvoicingOrderByColumnEnum
>[] => [
  {
    id: "id",
    meta: { label: "ID" },
    orderBy: RequestForInvoicingOrderByColumnEnum.Id,
    header: "ID",
    cell: ({ row }) => {
      const r = row.original;

      return (
        <div className="flex items-center">
          <Link
            href={{
              pathname: "/invoices/for-invoicing/edit",
              query: { id: r.id, step: "1" },
            }}
          >
            {r.id}
          </Link>
        </div>
      );
    },
  },
  {
    id: "type",
    meta: { label: t`Type` },
    header: () => t`Type`,
    cell: ({ row }) => (
      <div className="flex items-center">{startCase(row.original.type)}</div>
    ),
  },
  {
    id: "brands",
    meta: { label: t`Brands` },
    header: () => t`Brands`,
    cell: ({ row }) => {
      const r = row.original;

      return (
        <Flex align="center">
          <Box>
            {map(r.brands, (b) => (
              <React.Fragment key={b.id}>
                <Link
                  href={{ pathname: "/brands/detail", query: { id: b.id } }}
                >
                  {b.name}
                </Link>
                {last(r.brands)?.id === b.id ? null : ", "}
              </React.Fragment>
            ))}
          </Box>
        </Flex>
      );
    },
  },
  {
    id: "projects",
    meta: { label: t`Project/s` },
    header: () => t`Project/s`,
    cell: ({ row }) => {
      const r = row.original;

      return (
        <Flex>
          <Box>
            {map(r.projects, (p) => (
              <React.Fragment key={p.id}>
                <span title={`${p.code} – ${p.title.replace(" ", "-")}`}>
                  <Link
                    href={{ pathname: "/projects/detail", query: { id: p.id } }}
                  >
                    <div>{p.code}</div>
                    <div>{p.title}</div>
                  </Link>
                </span>
                <br />
              </React.Fragment>
            ))}
          </Box>
        </Flex>
      );
    },
  },
  {
    id: "totalAmount",
    meta: { align: "right", label: t`Amount` },
    header: () => t`Amount`,
    orderBy: RequestForInvoicingOrderByColumnEnum.Amount,
    cell: ({ row }) => currency.formatByWorkspace(row.original.totalAmount),
  },
  {
    id: "totalAmountWithVAT",
    meta: { align: "right", label: t`Amount with VAT` },
    header: () => t`Amount with VAT`,
    orderBy: RequestForInvoicingOrderByColumnEnum.AmountWithVat,
    cell: ({ row }) =>
      currency.formatByWorkspace(row.original.totalAmountWithVAT),
  },
  {
    id: "status",
    meta: { label: t`Status` },
    header: () => t`Status`,
    orderBy: RequestForInvoicingOrderByColumnEnum.Status,
    cell: ({ row }) => {
      const r = row.original;
      let color: string;
      switch (r.status) {
        case RequestForInvoicingStatusEnum.Submitted:
          color = "yellow";
          break;
        default:
          color = "grey";
          break;
      }

      return (
        <Tooltip
          isDisabled={
            r.status !== RequestForInvoicingStatusEnum.Submitted ||
            !r.submitDate
          }
          label={r.submitDate ? formatDate(new Date(r.submitDate)) : null}
        >
          <Badge colorScheme={color}>{r.status}</Badge>
        </Tooltip>
      );
    },
  },
  {
    id: "dateOfSupply",
    meta: { label: t`Supply date` },
    header: () => t`Supply date`,
    orderBy: RequestForInvoicingOrderByColumnEnum.DateOfSupply,
    cell: ({ row }) => {
      const { dateOfSupply } = row.original;

      return dateOfSupply ? formatDate(dateOfSupply) : null;
    },
  },
  {
    id: "created",
    meta: { label: t`Created` },
    header: () => t`Created`,
    orderBy: RequestForInvoicingOrderByColumnEnum.CreatedAt,
    cell: ({ row }) => formatDate(row.original.created),
  },
  {
    id: "paymentMethod",
    meta: { label: t`Payment method` },
    header: () => t`Payment method`,
    cell: ({ row }) => row.original.paymentMethod?.name ?? "—",
  },
  {
    id: "note",
    meta: { label: t`Note` },
    header: () => t`Note`,
    cell: ({ row }) => <TruncatedTextWithTooltip text={row.original.note} />,
  },
  {
    id: "internalNote",
    meta: { label: t`Internal note` },
    header: () => t`Internal note`,
    cell: ({ row }) => (
      <TruncatedTextWithTooltip text={row.original.internalNote} />
    ),
  },
  {
    id: ACTIONS_COLUMN_ID,
    meta: { label: t`Actions` },
    header: () => t`Actions`,
    cell: ({ row }) => <PopoverRowActions item={row.original} />,
  },
];

export const TableRequestsForInvoices = observer(
  function TableRequestsForInvoices() {
    const store = useContext(PageRequestsForInvoicingCtx);
    const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const data: TData[] = store.requestsForInvoicing.map((r) => {
      return {
        id: r.id,
        ownerId: r.ownerId,
        brands: r.brands,
        projects: r.projects,
        totalAmountWithVAT: r.stats.total_amount_with_vat,
        totalAmount: r.stats.total_amount,
        created: r.created_at,
        status: r.status,
        type: r.type,
        dateOfSupply: r.date_of_supply,
        submitDate: r.submitDate,
        note: r.note,
        internalNote: r.internal_note,
        paymentMethod: r.invoicePaymentMethod,
        actions: "",
      };
    });

    const [deleteRequestsForInvoicingBulk, deleteRequestsState] =
      useDeleteRequestsForInvoicingBulkMutation({
        ...onError(),
        onCompleted() {
          setIsDeleteModalOpen(false);
          store.selectedRequestsForInvoicingIds = [];
          store.refetch?.();
        },
      });

    const [submitRequestsForInvoicingBulk, submitRequestsState] =
      useSubmitRequestsForInvoicingBulkMutation({
        ...onError(),
        onCompleted() {
          store.selectedRequestsForInvoicingIds = [];
          store.refetch?.();
        },
      });

    const bulkActions = useMemo<BulkAction[]>(
      () => [
        {
          id: "requests-for-invoicing-actions",
          label: () => t`Actions`,
          icon: "dots-vertical",
          isLoading: () =>
            deleteRequestsState.loading || submitRequestsState.loading,
          subActions: [
            ...(can(
              "requestForInvoicing_delete_all",
              "requestForInvoicing_delete_own",
              "requestForInvoicing_delete_team",
            )
              ? [
                  {
                    id: "delete",
                    label: () => t`Delete`,
                    isDestructive: true,
                    callback: (selectedIds: string[] | Set<string>) => {
                      setIdsToDelete(Array.from(selectedIds));
                      setIsDeleteModalOpen(true);
                    },
                  },
                ]
              : []),
            ...(can(
              "requestForInvoicing_update_all",
              "requestForInvoicing_update_own",
              "requestForInvoicing_update_team",
            ) &&
            store.statusFilter.value[0] !==
              RequestForInvoicingStatusEnum.Submitted
              ? [
                  {
                    id: "submit",
                    label: () => t`Submit`,
                    callback: (selectedIds: string[] | Set<string>) => {
                      submitRequestsForInvoicingBulk({
                        variables: { id: Array.from(selectedIds) },
                      });
                    },
                  },
                ]
              : []),
          ],
        },
      ],
      [
        deleteRequestsState.loading,
        store.statusFilter.value,
        submitRequestsState.loading,
        submitRequestsForInvoicingBulk,
      ],
    );

    return (
      <React.Fragment>
        <DataTable.Body
          tableKey="requests-for-invoicing"
          data={data}
          columns={getColumns()}
          enableRowSelection
          bulkActions={bulkActions}
          orderedBy={store.orderBy.clauses[0]}
          onOrderByChange={(clause) => {
            if (!clause.order) {
              store.orderBy.setClauses([]);
              return;
            }

            store.orderBy.setClauses([
              { column: clause.column, order: clause.order },
            ]);
          }}
          selected={store.selectedRequestsForInvoicingIds}
          onSelect={(ids) => (store.selectedRequestsForInvoicingIds = ids)}
          allIds={store.allRequestsForInvoicingIds}
        />
        <ModalConfirm
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() =>
            deleteRequestsForInvoicingBulk({
              variables: { id: idsToDelete },
            })
          }
          isLoading={deleteRequestsState.loading}
        />
      </React.Fragment>
    );
  },
);
